import { type ShouldRevalidateFunctionArgs } from 'react-router';

import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18N_LOCALES } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { CreateListingApplicationFromTmpStorageContainer } from '#/modules/listing/containers/CreateListingApplicationFromTmpStorageContainer';
import { isListingExpired } from '#/modules/listing/helpers/listingTiming';
import { listingApiServerFetchJob } from '#/modules/listing/jobs/api/listingApiFetchJob';
import { viewJobReferenceRoute } from '#/modules/listing/routing/listing.routes';
import { DynamicSearchJobsRefinements } from '#/modules/search/jobs/components/Refinements/DynamicSearchJobsRefinements';
import { userDashboardApiServerFetchListingExtras } from '#/modules/userDashboard/api/listingExtras/userDashboardApiFetchListingExtras';
import { DynamicNotFoundPage } from '#/pages/NotFound/DynamicNotFoundPage';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { idealistNotFound } from '#/routing/utils/idealistNotFound';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { dynamic } from '#/utils/dynamic';
import { imageUrlForOgMeta } from '#/utils/filestack';

import { ViewJobPage } from './ViewJobPage';

export const handle = {
  defaultSearchType: 'JOB',
  showDesktopSearch: true,
  showSearchRefinements: true,
  searchRefinements: <DynamicSearchJobsRefinements />,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedListingApplicationTmpId =
    currentUrl.searchParams.has('listingApplicationTmpId') &&
    !nextUrl.searchParams.has('listingApplicationTmpId');

  if (removedListingApplicationTmpId) return false;

  return defaultShouldRevalidate;
}

const DynamicExpiredJobPage = dynamic(
  () =>
    import('#/modules/listing/jobs/pages/expired/ExpiredJobPage').then((m) => ({
      default: m.ExpiredJobPage,
    })),
  { strategy: 'always' },
);

const DynamicUnpublishedJobPage = dynamic(
  () =>
    import('#/modules/listing/jobs/pages/unpublished/UnpublishedJobPage').then(
      (m) => ({
        default: m.UnpublishedJobPage,
      }),
    ),
  { strategy: 'always' },
);

export async function loader({
  request,
  context,
  params: { jobIdDashSlug },
}: HorizonAppRouteLoaderArgs<typeof viewJobReferenceRoute>) {
  const { serverContext } = getServerRouterContext(context);

  const [jobId] = jobIdDashSlug.split('-');

  try {
    const [job, listingExtras] = await Promise.all([
      listingApiServerFetchJob({ request, context })(jobId),
      serverContext.hasNoCacheCookie
        ? userDashboardApiServerFetchListingExtras({
            request,
            context,
          })([jobId])
        : null,
    ]);

    const { saved, applicationId } = listingExtras?.[jobId] ?? {
      saved: false,
      applicationId: null,
    };

    switch (job.type) {
      case 'JOB': {
        return {
          type: isListingExpired({
            expires: job.expires,
            now: serverContext.now,
          })
            ? 'EXPIRED_JOB'
            : job.type,
          job,
          saved,
          applicationId,
          locale: serverContext.locale,
          handle: {
            hideFooterDivider: 'hide-footer-divider-if-logged-out',
            searchFromPageType: 'LISTING',
            urlByLocale: job.url,
          } satisfies LayoutHandleProps,
          now: serverContext.now,
        } as const;
      }
      case 'UNPUBLISHED_JOB':
        return idealistNotFound({
          type: 'UNPUBLISHED_JOB',
          unpublishedJob: job,
        } as const);
    }
  } catch {
    return idealistNotFound(null);
  }
}

export default function ViewJobHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  // NOTE: UNPUBLISHED_JOB is not the same as Job.isPublished === false
  // UNPUBLISHED_JOB is returned when we are serving an unpublished (hidden) job
  // to a user who has not applied to it.
  // Previous Applicants would fall under the JOB branch (Job.type === 'JOB')
  // Context: https://idealist.slack.com/archives/CHJ9JK7TP/p1749664131909249?thread_ts=1747839567.113479&cid=CHJ9JK7TP
  switch (loaderData?.type) {
    case undefined:
      return <DynamicNotFoundPage />;
    case 'JOB': {
      const orgName = loaderData.job.isPostedAnonymously
        ? loaderData.job.orgNameOverride
        : loaderData.job.org?.name;

      return (
        <RouteModule
          title={`${loaderData.job.name} - ${LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)['JOB']}`}
          ogImage={
            loaderData.job.isPostedAnonymously
              ? null
              : imageUrlForOgMeta({
                  data:
                    loaderData.job.orgLogoOverride ?? loaderData.job.org?.logo,
                })
          }
          description={
            orgName
              ? i18n(
                  '%(org_name)s is now hiring for the position of %(job_name)s in %(city)s. Apply today.',
                  {
                    org_name: orgName,
                    job_name: loaderData.job.name,
                    city: loaderData.job.address.city,
                  },
                )
              : i18n(
                  'Employers are now hiring for the position of %(job_name)s in %(city)s. Apply today.',
                  {
                    job_name: loaderData.job.name,
                    city: loaderData.job.address.city,
                  },
                )
          }
          linkRels={{
            translations: I18N_LOCALES.map((locale) => {
              const url = loaderData.job.url[locale];
              if (url) return { url, locale };
            }).filter(Boolean),
            canonical: loaderData.job.url[loaderData.job.locale],
          }}
          schemaMarkups={[
            loaderData.job.schemaJobMarkup,
            loaderData.job.schemaBreadcrumbListMarkup?.[loaderData.locale],
          ]}
        >
          <CreateListingApplicationFromTmpStorageContainer
            listing={loaderData.job}
          >
            {({ initialListingApplicationId, listingApplicationFailure }) => (
              <ViewJobPage
                job={loaderData.job}
                initialListingApplicationId={
                  initialListingApplicationId ?? loaderData.applicationId
                }
                initiallySaved={loaderData.saved}
                listingApplicationFailure={listingApplicationFailure}
                now={loaderData.now}
              />
            )}
          </CreateListingApplicationFromTmpStorageContainer>
        </RouteModule>
      );
    }
    case 'EXPIRED_JOB':
      return (
        <RouteModule title={loaderData.job.name}>
          <DynamicExpiredJobPage
            job={loaderData.job}
            listingApplicationId={loaderData.applicationId}
            now={loaderData.now}
          />
        </RouteModule>
      );
    case 'UNPUBLISHED_JOB':
      return (
        <RouteModule title={i18n('Listing Unpublished')}>
          <DynamicUnpublishedJobPage
            unpublishedJob={loaderData.unpublishedJob}
          />
        </RouteModule>
      );
  }
}
